import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Button,
  Form,
  Input,
  InputNumber,
  Radio,
  Checkbox,
  Modal,
  Popconfirm,
  Spin,
  notification,
  Empty,
  Tag,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
  PictureOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import {
  getQuizByContentId,
  addQuizQuestion,
  updateQuizQuestion,
  deleteQuizQuestion,
  uploadQuizQuestionImage
} from '../../../../../../Constants/Api/functions/function';
import { BASE_URL } from '../../../../../../Constants/Api/azure/azure';
import './style.scss';

const { TextArea } = Input;

const formatImgUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const origin = BASE_URL.replace(/\/api\/?$/, '');
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const QuizManager = ({ open, onClose, contentData, courseId, onQuizUpdated }) => {
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionType, setQuestionType] = useState('single');
  const [selectedCorrect, setSelectedCorrect] = useState([0]);
  const [optionCount, setOptionCount] = useState(4); // default 4 options (A, B, C, D)
  const [imageUrl, setImageUrl] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && contentData?.id) {
      loadQuiz();
    }
  }, [open, contentData]);

  const loadQuiz = async () => {
    if (!contentData?.id) return;
    setIsLoading(true);
    try {
      const res = await getQuizByContentId(contentData.id, null, 'admin');
      if (res && res.success) {
        setQuizData(res.quiz);
        setQuestions(res.questions || []);
      } else {
        setQuizData(null);
        setQuestions([]);
      }
    } catch (err) {
      console.error('Error loading quiz:', err);
    }
    setIsLoading(false);
  };

  const handleOpenAdd = () => {
    setEditingQuestion(null);
    setQuestionType('single');
    setSelectedCorrect([0]);
    setOptionCount(4);
    setImageUrl('');
    form.resetFields();
    form.setFieldsValue({
      questionType: 'single',
      marks: 1,
      option1: '',
      option2: '',
      option3: '',
      option4: '',
      option5: '',
      explanation: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (q) => {
    setEditingQuestion(q);
    const qType = q.questionType || 'single';
    setQuestionType(qType);
    const correctArr = Array.isArray(q.correctAnswers) ? q.correctAnswers : [0];
    setSelectedCorrect(correctArr);
    setImageUrl(q.image || '');

    // Calculate how many options are present
    const rawOpts = [q.option1, q.option2, q.option3, q.option4, q.option5];
    let count = 2;
    for (let i = 4; i >= 2; i--) {
      if (rawOpts[i] !== null && rawOpts[i] !== undefined && rawOpts[i] !== '') {
        count = i + 1;
        break;
      }
    }
    setOptionCount(Math.max(2, count));

    form.setFieldsValue({
      questionText: q.questionText,
      questionType: qType,
      marks: q.marks,
      option1: q.option1,
      option2: q.option2,
      option3: q.option3 || '',
      option4: q.option4 || '',
      option5: q.option5 || '',
      explanation: q.explanation || ''
    });
    setIsModalOpen(true);
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    try {
      const res = await uploadQuizQuestionImage(file);
      if (res && res.success) {
        setImageUrl(res.url || res.filePath);
        api.success({
          message: 'Image Attached',
          description: 'Question image uploaded successfully.'
        });
      } else {
        api.error({
          message: 'Upload Failed',
          description: res.message || 'Could not upload question image.'
        });
      }
    } catch (err) {
      console.error('Question image upload error:', err);
      api.error({
        message: 'Upload Error',
        description: 'An error occurred while uploading image.'
      });
    }
    setIsUploadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = () => {
    setImageUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddOption = () => {
    if (optionCount < 5) {
      setOptionCount(prev => prev + 1);
    }
  };

  const handleRemoveOption = (indexToRemove) => {
    if (optionCount <= 2) return;
    // Clear value in form
    form.setFieldsValue({
      [`option${indexToRemove + 1}`]: ''
    });
    // Adjust selected correct answers
    setSelectedCorrect(prev => {
      const filtered = prev.filter(idx => idx !== indexToRemove && idx < optionCount - 1);
      return filtered.length > 0 ? filtered : [0];
    });
    setOptionCount(prev => prev - 1);
  };

  const handleDelete = async (questionId) => {
    setIsLoading(true);
    try {
      const res = await deleteQuizQuestion(questionId);
      if (res && res.success) {
        api.success({
          message: 'Question Deleted',
          description: 'The question was successfully deleted.'
        });
        await loadQuiz();
        if (onQuizUpdated) onQuizUpdated();
      } else {
        api.error({ message: 'Error', description: res.message || 'Failed to delete question' });
      }
    } catch (err) {
      console.error('Delete question error:', err);
    }
    setIsLoading(false);
  };

  const handleSaveQuestion = async (values) => {
    const validCorrect = selectedCorrect.filter(idx => idx < optionCount);
    if (validCorrect.length === 0) {
      api.warning({
        message: 'Missing Correct Answer',
        description: 'Please select at least one correct answer for this question.'
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        questionText: values.questionText,
        image: imageUrl || null,
        questionType: questionType,
        marks: values.marks,
        option1: values.option1,
        option2: values.option2,
        option3: optionCount >= 3 ? (values.option3 || null) : null,
        option4: optionCount >= 4 ? (values.option4 || null) : null,
        option5: optionCount >= 5 ? (values.option5 || null) : null,
        correctAnswers: validCorrect,
        explanation: values.explanation || null,
        courseContentId: contentData.id,
        courseId: courseId,
        quizId: quizData?.id,
        orderNumber: editingQuestion ? editingQuestion.orderNumber : questions.length + 1
      };

      let res;
      if (editingQuestion) {
        payload.id = editingQuestion.id;
        res = await updateQuizQuestion(payload);
      } else {
        res = await addQuizQuestion(payload);
      }

      if (res && res.success) {
        api.success({
          message: editingQuestion ? 'Question Updated' : 'Question Added',
          description: 'Quiz question has been saved successfully.'
        });
        setIsModalOpen(false);
        await loadQuiz();
        if (onQuizUpdated) onQuizUpdated();
      } else {
        api.error({ message: 'Error', description: res.message || 'Failed to save question' });
      }
    } catch (err) {
      console.error('Save question error:', err);
      api.error({ message: 'Error', description: 'An error occurred while saving.' });
    }
    setIsLoading(false);
  };

  const totalMarks = questions.reduce((sum, q) => sum + (parseFloat(q.marks) || 0), 0);

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <QuestionCircleOutlined />
          <span>Manage Quiz: {contentData?.title || 'Lesson'}</span>
        </div>
      }
      placement="right"
      width={740}
      onClose={onClose}
      open={open}
      className="quiz-manager-drawer"
      destroyOnClose
    >
      {contextHolder}
      <Spin spinning={isLoading} tip="Loading quiz...">
        <div className="quiz-summary-bar">
          <div className="summary-stats">
            <div className="stat-item">
              <span className="label">Total Questions</span>
              <span className="value">{questions.length}</span>
            </div>
            <div className="stat-item">
              <span className="label">Total Marks</span>
              <span className="value">{totalMarks.toFixed(1)}</span>
            </div>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleOpenAdd}
            style={{ borderRadius: 8 }}
          >
            Add Question
          </Button>
        </div>

        {questions.length === 0 ? (
          <div className="no-questions">
            <Empty
              description={
                <div>
                  <h3>No questions added yet</h3>
                  <p>Create a quiz for this course content with questions, flexible answers (2-5 options), images, and custom marks.</p>
                </div>
              }
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenAdd}>
                Add First Question
              </Button>
            </Empty>
          </div>
        ) : (
          <div className="questions-list">
            {questions.map((q, idx) => {
              const qCorrect = Array.isArray(q.correctAnswers) ? q.correctAnswers : [];
              const rawOpts = [q.option1, q.option2, q.option3, q.option4, q.option5];
              const displayOpts = rawOpts.filter(opt => opt !== null && opt !== undefined && opt !== '');

              return (
                <div key={q.id || idx} className="question-card">
                  <div className="card-header">
                    <div className="q-meta">
                      <span className="q-badge">Q{idx + 1}</span>
                      <span className="q-type">
                        {q.questionType === 'multiple' ? 'Multiple Choice' : 'Single Choice'}
                      </span>
                      <span className="q-marks">{q.marks} {Number(q.marks) === 1 ? 'Mark' : 'Marks'}</span>
                      <Tag color="cyan">{displayOpts.length} Answers</Tag>
                    </div>
                    <div className="card-actions">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenEdit(q)}
                        size="small"
                      >
                        Edit
                      </Button>
                      <Popconfirm
                        title="Delete Question"
                        description="Are you sure you want to delete this question?"
                        onConfirm={() => handleDelete(q.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button type="text" danger icon={<DeleteOutlined />} size="small">
                          Delete
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>

                  <div className="question-text">{q.questionText}</div>

                  {q.image && (
                    <div className="question-image-preview" style={{ marginBottom: 14 }}>
                      <img
                        src={formatImgUrl(q.image)}
                        alt="Question Diagram"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 220,
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                  )}

                  <div className="options-grid">
                    {displayOpts.map((optText, optIdx) => {
                      const isCorrect = qCorrect.includes(optIdx);
                      return (
                        <div key={optIdx} className={`option-row ${isCorrect ? 'correct' : ''}`}>
                          <span className="opt-badge">{String.fromCharCode(65 + optIdx)}</span>
                          <span className="opt-text">{optText}</span>
                          {isCorrect && (
                            <span className="correct-tag">
                              <CheckCircleOutlined style={{ marginRight: 4 }} /> Correct Answer
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {q.explanation && (
                    <div className="explanation-box">
                      <strong>Explanation: </strong> {q.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Spin>

      {/* Add / Edit Question Modal */}
      <Modal
        title={editingQuestion ? 'Edit Quiz Question' : 'Add New Quiz Question'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
        className="question-form-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveQuestion}
          initialValues={{
            questionType: 'single',
            marks: 1
          }}
        >
          <Form.Item
            name="questionText"
            label="Question Text (Supports Sinhala, Tamil, English, and any Unicode script)"
            rules={[{ required: true, message: 'Please enter the question text' }]}
          >
            <TextArea
              rows={3}
              placeholder="Enter your question here (e.g. ප්‍රශ්නය මෙහි ඇතුළත් කරන්න)..."
              style={{ fontSize: '1rem', lineHeight: 1.5 }}
            />
          </Form.Item>

          {/* Question Image Attachment */}
          <div style={{ marginBottom: 18, background: '#f8fafc', padding: 14, borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                <PictureOutlined style={{ color: '#6366f1' }} />
                Question Image / Diagram (Optional)
              </span>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageFileChange}
              />
              <Button
                size="small"
                icon={<UploadOutlined />}
                loading={isUploadingImage}
                onClick={() => fileInputRef.current?.click()}
              >
                {imageUrl ? 'Change Image' : 'Attach Image'}
              </Button>
            </div>

            {imageUrl ? (
              <div style={{ position: 'relative', display: 'inline-block', marginTop: 6 }}>
                <img
                  src={formatImgUrl(imageUrl)}
                  alt="Question Diagram Preview"
                  style={{
                    maxHeight: 160,
                    maxWidth: '100%',
                    borderRadius: 8,
                    border: '1px solid #cbd5e1',
                    objectFit: 'contain'
                  }}
                />
                <Button
                  danger
                  size="small"
                  type="primary"
                  icon={<DeleteOutlined />}
                  onClick={handleRemoveImage}
                  style={{ position: 'absolute', top: 6, right: 6 }}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Add diagrams, illustrations, charts, or formula snapshots to display alongside the question.
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Form.Item
              name="questionType"
              label="Question Answer Type"
              rules={[{ required: true }]}
              style={{ flex: 1, minWidth: 240 }}
            >
              <Radio.Group
                onChange={(e) => {
                  const newType = e.target.value;
                  setQuestionType(newType);
                  if (newType === 'single') {
                    setSelectedCorrect(selectedCorrect.length > 0 ? [selectedCorrect[0]] : [0]);
                  }
                }}
              >
                <Radio value="single">Single Choice (1 Correct Answer)</Radio>
                <Radio value="multiple">Multiple Choice (Multiple Correct Answers)</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name="marks"
              label="Marks / Points"
              rules={[{ required: true, message: 'Please set marks' }]}
              style={{ width: 140 }}
            >
              <InputNumber min={0.5} step={0.5} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          {/* Flexible Options (2 to 5) */}
          <div className="options-form-card">
            <div className="options-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span>Answer Options ({optionCount} Options)</span>
                <span style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', fontWeight: 'normal', marginTop: 2 }}>
                  {questionType === 'single'
                    ? 'Select the radio button for the single correct answer'
                    : 'Check all checkboxes for the correct answers'}
                </span>
              </div>
              {optionCount < 5 && (
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={handleAddOption}
                  style={{ color: '#4f46e5', borderColor: '#818cf8' }}
                >
                  Add Option ({String.fromCharCode(65 + optionCount)})
                </Button>
              )}
            </div>

            {Array.from({ length: optionCount }).map((_, idx) => {
              const letter = String.fromCharCode(65 + idx);
              const isChecked = selectedCorrect.includes(idx);
              const isRemovable = optionCount > 2 && idx === optionCount - 1;

              return (
                <div key={idx} className="option-input-item">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {questionType === 'single' ? (
                        <Radio
                          checked={isChecked}
                          onChange={() => setSelectedCorrect([idx])}
                        >
                          <strong>Option {letter} (Correct Answer)</strong>
                        </Radio>
                      ) : (
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCorrect([...selectedCorrect, idx]);
                            } else {
                              setSelectedCorrect(selectedCorrect.filter((i) => i !== idx));
                            }
                          }}
                        >
                          <strong>Option {letter} (Correct Answer)</strong>
                        </Checkbox>
                      )}
                    </div>

                    {isRemovable && (
                      <Tooltip title={`Remove Option ${letter}`}>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<MinusCircleOutlined />}
                          onClick={() => handleRemoveOption(idx)}
                        >
                          Remove
                        </Button>
                      </Tooltip>
                    )}
                  </div>

                  <Form.Item
                    name={`option${idx + 1}`}
                    style={{ marginTop: 6, marginBottom: 12 }}
                    rules={[{ required: true, message: `Please enter text for Option ${letter}` }]}
                  >
                    <Input placeholder={`Enter text for Option ${letter}...`} style={{ fontSize: '0.95rem' }} />
                  </Form.Item>
                </div>
              );
            })}
          </div>

          <Form.Item name="explanation" label="Explanation (Optional - shown to students after submitting)">
            <TextArea rows={2} placeholder="Explain why the answer is correct or provide guidance in any language..." />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {editingQuestion ? 'Update Question' : 'Save Question'}
            </Button>
          </div>
        </Form>
      </Modal>
    </Drawer>
  );
};

export default QuizManager;
